import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from "@/context/auth";
import Button from "@/components/button";
import { Footer } from "@/components/footer";
import { AlertCircle, CheckCircle2 } from "lucide-react";

function StripeCallback() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [returnUrl, setReturnUrl] = useState<string | null>(null);
  const [resolvedFrom, setResolvedFrom] = useState<string | null>(null);

  function navigateToDashboard() {
    const role = user?.role;
    if (role === 'employer' || role === 'worker') {
      navigate({ to: '/app/$role/dashboard', params: { role } });
    } else {
      navigate({ to: '/' });
    }
  }

  useEffect(() => {
    console.log('Stripe callback received', {
      searchParams: window.location.search,
      hash: window.location.hash
    });

    const urlParams = new URLSearchParams(window.location.search);
    const setupIntentId = urlParams.get('setup_intent');
    const setupIntentClientSecret = urlParams.get('setup_intent_client_secret');
    const redirectStatus = urlParams.get('redirect_status');
    const fromParam = urlParams.get('from');
    const businessId = urlParams.get('business_id');
    const accountId = urlParams.get('account'); // Connect account ID for hosted onboarding

    console.log('Parsed URL parameters:', {
      setupIntentId,
      setupIntentClientSecret,
      redirectStatus,
      fromParam,
      businessId,
      accountId
    });

    if (fromParam) {
      setResolvedFrom(decodeURIComponent(fromParam));
    }

    const role = user?.role;

    function doNavigate() {
      if (fromParam) {
        navigate({ to: decodeURIComponent(fromParam) as '/' });
      } else if (role === 'employer' || role === 'worker') {
        navigate({ to: '/app/$role/dashboard', params: { role } });
      } else {
        navigate({ to: '/' });
      }
    }

    // Handle Connect hosted onboarding completion
    if (accountId && redirectStatus === 'succeeded') {
      setStatus('success');
      setMessage(t('stripeCallback.connectAchSuccessMessage'));

      // Optionally poll for webhook confirmation or just proceed
      setTimeout(() => {
        doNavigate();
      }, 3000);
    } else if (accountId && redirectStatus === 'failed') {
      setStatus('error');
      setMessage(t('stripeCallback.connectFailedMessage'));
    } else if (redirectStatus === 'succeeded') {
      // Handle regular setup intent success (legacy)
      setStatus('success');
      setMessage(t('stripeCallback.setupSuccessMessage'));
      setTimeout(() => {
        doNavigate();
      }, 2000);
    } else if (redirectStatus === 'failed') {
      setStatus('error');
      setMessage(t('stripeCallback.setupFailedMessage'));
    } else if (!setupIntentId && !accountId) {
      // No valid callback, redirect to dashboard
      console.log('No setup intent or account found, redirecting to dashboard');
      doNavigate();
    }

    // If there's a return URL parameter, decode and store it
    const returnUrlParam = urlParams.get('return_url');
    if (returnUrlParam) {
      try {
        const decodedReturnUrl = decodeURIComponent(returnUrlParam);
        setReturnUrl(decodedReturnUrl);
        console.log('Found return URL:', decodedReturnUrl);
      } catch (error) {
        console.error('Failed to decode return URL:', error);
      }
    }

    // If setup intent is present, we would normally process it here
    // But for hosted onboarding, Stripe handles the result
    if (setupIntentId) {
      console.log('Setup intent found:', setupIntentId);
    }

  }, [navigate, user, returnUrl, t]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="max-w-md w-full mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">{t('stripeCallback.processing')}</h2>
              <p className="text-gray-600">{t('stripeCallback.processingWaitStripe')}</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="text-center bg-white rounded-lg shadow-sm p-8">
            {status === 'success' ? (
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            ) : (
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            )}

            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              {status === 'success' ? t('stripeCallback.setupCompleteTitle') : t('stripeCallback.setupFailedTitle')}
            </h2>

            <p className="text-gray-600 mb-6">{message}</p>

            <div className="space-y-3">
              <Button
                onClick={() => {
                  if (resolvedFrom) {
                    navigate({ to: resolvedFrom as '/' });
                  } else {
                    navigateToDashboard();
                  }
                }}
                className="w-full"
              >
                {t('stripeCallback.continueToDashboard')}
              </Button>

              {status === 'error' && (
                <Button
                  variant="outline"
                  onClick={() => {
                    // Retry logic or contact support
                    window.location.href = 'mailto:support@publicmarket.com';
                  }}
                  className="w-full"
                >
                  {t('stripeCallback.contactSupport')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/callback/stripe')({
  component: StripeCallback,
});
